import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Flex } from 'foundations-components/components/Flex';
import { Modal } from 'foundations-components/transitional/components/Modal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { useTheme } from 'foundations-theming/utils';
import CustomColorButton from 'payments-checkout-lib/components/Utilities/CustomColorButton';
import { getTextColorWithTheme } from 'payments-checkout-lib/utils/colors';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { SEPA } from 'payments-checkout-lib/constants/PaymentElement';
import AchModalTerms from './AchModalTerms';
import SepaModalTerms from 'payments-checkout-lib/components/AuthorizationModal/SepaModalTerms';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CustomColorUIDialogHeader = styled(UIDialogHeader).withConfig({
  displayName: "PaymentAuthorizationModal__CustomColorUIDialogHeader"
})(["color:", " !important;border-color:", " !important;background-color:", " !important;background-image:none !important;"], ({
  primaryColor,
  theme
}) => getTextColorWithTheme(primaryColor, theme), ({
  primaryColor
}) => primaryColor, ({
  primaryColor
}) => primaryColor);

// @bend-expect trellis-tokenized
const CustomColorUIDialogCloseButton = styled(UIDialogCloseButton).withConfig({
  displayName: "PaymentAuthorizationModal__CustomColorUIDialogCloseButton"
})(["color:", " !important;"], ({
  primaryColor,
  theme
}) => getTextColorWithTheme(primaryColor, theme));
const PaymentAuthorizationModal = ({
  checkoutSession,
  error,
  loading,
  onConfirm,
  onReject,
  selectedOptionalLineItems
}) => {
  var _checkoutSession$user;
  const theme = useTheme();
  const {
    formState
  } = usePaymentFormContext();
  const paymentMethod = formState.paymentMethod;
  const primaryColor = ((_checkoutSession$user = checkoutSession.userDefinedBranding) === null || _checkoutSession$user === void 0 ? void 0 : _checkoutSession$user.primaryColor) || theme.color['fill-accent-neutral-default'];
  const modalTerms = useMemo(() => {
    if (paymentMethod === SEPA) {
      return /*#__PURE__*/_jsx(SepaModalTerms, {});
    } else {
      return /*#__PURE__*/_jsx(AchModalTerms, {
        checkoutSession: checkoutSession,
        selectedOptionalLineItems: selectedOptionalLineItems
      });
    }
  }, [checkoutSession, paymentMethod, selectedOptionalLineItems]);
  const footer = useMemo(() => /*#__PURE__*/_jsxs(DialogFooter, {
    children: [/*#__PURE__*/_jsx(CustomColorButton, {
      style: {
        width: '100%'
      },
      Component: UILoadingButton,
      primaryColor: primaryColor,
      loading: loading,
      failed: !!error,
      type: "submit",
      onClick: onConfirm,
      "data-test-id": "authorization-modal-confirm-button",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(LockedIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentElementAuthModal.authorize",
          "data-test-id": "authorization-modal-confirm-message"
        })]
      })
    }), /*#__PURE__*/_jsx(CustomColorButton, {
      style: {
        width: '100%'
      },
      Component: UIButton,
      primaryColor: primaryColor,
      use: "primary-color-secondary",
      onClick: onReject,
      disabled: loading,
      "data-test-id": "authorization-modal-cancel-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.paymentElementAuthModal.cancel"
      })
    })]
  }), [error, loading, onConfirm, onReject, primaryColor]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "payment-auth-modal",
    children: [/*#__PURE__*/_jsxs(CustomColorUIDialogHeader, {
      primaryColor: primaryColor,
      theme: theme,
      children: [/*#__PURE__*/_jsx(CustomColorUIDialogCloseButton, {
        primaryColor: primaryColor,
        theme: theme,
        onClick: onReject,
        disabled: loading,
        "data-test-id": "authorization-modal-close-button"
      }), /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentElementAuthModal.title"
        })
      })]
    }), modalTerms, footer]
  });
};
export default PaymentAuthorizationModal;